import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Map as MapIcon, RotateCcw, WandSparkles } from 'lucide-react';
import { useAuth } from 'shared/hooks/useAuth';
import { useArenaProfile } from 'shared/contexts/ArenaProfileContext';
import { ARENA_OWNER_SIGNUP, ARENA_OWNER_SIGNUP_AUTO, ROUTES } from 'shared/constants/routes';
import { EASE_OUT } from 'shared/components/motion/tokens';
import { cn } from 'shared/utils/cn';

const TOUR_KEY = 'marco.demo_tour';

interface TourState {
  visited: string[];
  open: boolean;
  /** Já abrimos o roteiro sozinhos depois do cadastro da arena (só acontece uma vez). */
  autoOpened?: boolean;
}

// Começa recolhido (só a pílula): aberto por padrão ele cobriria a home de quem só está visitando.
const INITIAL_TOUR: TourState = { visited: [], open: false };

function readTour(): TourState {
  try {
    const raw = localStorage.getItem(TOUR_KEY);
    if (raw) return { ...INITIAL_TOUR, ...JSON.parse(raw) };
  } catch {
    // sem storage: começa do zero
  }
  return INITIAL_TOUR;
}

// Roteiro da demonstração para o dono de arena: criar conta → cadastrar a arena → usar o
// painel → ver a própria arena como jogador. Cada passo se marca sozinho quando acontece.
export function DemoGuide() {
  const { user, impersonate, logout } = useAuth();
  const { profile, isCustom, resetDemo } = useArenaProfile();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [tour, setTour] = useState<TourState>(readTour);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const hasOwnAccount = user?.role === 'arena' && !user.id.startsWith('demo-');
  // Nas telas de acesso o roteiro fica só como pílula: ele cobriria o painel da marca, e
  // essas telas já SÃO o primeiro passo do roteiro.
  const isAuthPage = pathname === ROUTES.LOGIN || pathname === ROUTES.SIGNUP;
  const [openOnAuthPage, setOpenOnAuthPage] = useState(false);
  useEffect(() => setOpenOnAuthPage(false), [pathname]);
  const isOpen = isAuthPage ? openOnAuthPage : tour.open;

  // Marca as telas visitadas. "Ver como jogador" só conta depois que existe uma arena própria.
  useEffect(() => {
    const isPlayerView = pathname === ROUTES.HOME && user?.role !== 'arena' && isCustom;
    const key = isPlayerView ? 'marketplace' : pathname;
    setTour((current) => (current.visited.includes(key) ? current : { ...current, visited: [...current.visited, key] }));
  }, [pathname, user?.role, isCustom]);

  // Quando a arena acaba de ser cadastrada, abre o roteiro uma vez: é o momento em que o aviso
  // "siga o roteiro da demonstração" aparece e os próximos passos passam a fazer sentido.
  useEffect(() => {
    if (isCustom && !tour.autoOpened) setTour((current) => ({ ...current, open: true, autoOpened: true }));
  }, [isCustom, tour.autoOpened]);

  useEffect(() => {
    try {
      localStorage.setItem(TOUR_KEY, JSON.stringify(tour));
    } catch {
      // ignora: o roteiro só não lembra o progresso
    }
  }, [tour]);

  const steps = [
    {
      id: 'conta',
      title: 'Crie sua conta de dono de arena',
      hint: 'Nome, e-mail e senha — leva 30 segundos.',
      done: hasOwnAccount || isCustom,
      go: () => navigate(ARENA_OWNER_SIGNUP),
    },
    {
      id: 'arena',
      title: 'Cadastre sua arena',
      hint: 'Quadras, esportes, horários, equipe e plano.',
      done: isCustom,
      go: () => navigate(ROUTES.ARENA_ONBOARDING),
    },
    {
      id: 'agenda',
      title: 'Veja a agenda do dia',
      hint: 'Suas quadras com reservas, aulas e bloqueios.',
      done: tour.visited.includes(ROUTES.ARENA_DASHBOARD),
      go: () => navigate(ROUTES.ARENA_DASHBOARD),
    },
    {
      id: 'reservas',
      title: 'Cobre uma reserva pendente',
      hint: 'Pix dividido entre os jogadores, sem comprovante.',
      done: tour.visited.includes(ROUTES.ARENA_RESERVATIONS),
      go: () => navigate(ROUTES.ARENA_RESERVATIONS),
    },
    {
      id: 'relatorios',
      title: 'Acompanhe os relatórios',
      hint: 'Receita, ocupação por quadra, clientes e mais.',
      done: tour.visited.includes(ROUTES.ARENA_REPORTS),
      go: () => navigate(ROUTES.ARENA_REPORTS),
    },
    {
      id: 'jogador',
      title: 'Veja sua arena no app do jogador',
      hint: isCustom ? `${profile.arenaName} já aparece para reservar.` : 'Disponível depois de cadastrar a arena.',
      done: tour.visited.includes('marketplace'),
      go: () => {
        impersonate('jogador');
        navigate(ROUTES.HOME);
      },
    },
  ];

  const doneCount = steps.filter((step) => step.done).length;
  const nextStep = steps.find((step) => !step.done);
  const progress = (doneCount / steps.length) * 100;

  async function restart() {
    await logout();
    resetDemo();
    setTour(INITIAL_TOUR);
    setConfirmingReset(false);
    navigate(ARENA_OWNER_SIGNUP);
  }

  const setOpen = (open: boolean) =>
    isAuthPage ? setOpenOnAuthPage(open) : setTour((current) => ({ ...current, open }));

  return (
    <div
      className={cn(
        'fixed z-40 print:hidden',
        // Nas telas de acesso o canto esquerdo é o rodapé do formulário: vai para a direita.
        isAuthPage ? 'bottom-4 right-4 sm:bottom-6 sm:right-6' : 'bottom-20 left-4 sm:bottom-6 sm:left-6'
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        {isOpen ? (
          <motion.section
            key="open"
            aria-label="Roteiro da demonstração"
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="w-[min(340px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border/60 bg-surface/95 shadow-card-hover backdrop-blur"
          >
            <header className="flex items-start justify-between gap-3 px-4 pb-3 pt-4">
              <div>
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary-dark">
                  <MapIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Roteiro da demonstração
                </p>
                <p className="mt-1 text-sm text-ink-secondary">
                  {doneCount === steps.length
                    ? 'Pronto! Você viu o fluxo completo.'
                    : `${doneCount} de ${steps.length} passos concluídos`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-1 text-ink-secondary hover:bg-chip hover:text-ink"
                aria-label="Recolher roteiro"
              >
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
            </header>

            <div className="mx-4 h-1.5 overflow-hidden rounded-full bg-chip">
              <motion.div
                className="h-full rounded-full bg-primary"
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: EASE_OUT }}
              />
            </div>

            <ol className="mt-3 flex flex-col px-2">
              {steps.map((step, index) => {
                const isNext = step === nextStep;
                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      onClick={step.go}
                      className={cn(
                        'flex w-full items-start gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-chip',
                        isNext && 'bg-accent-soft/60 hover:bg-accent-soft'
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
                          step.done
                            ? 'bg-success text-surface'
                            : isNext
                              ? 'bg-primary text-surface'
                              : 'border border-border text-ink-secondary'
                        )}
                      >
                        {step.done ? <Check className="h-3 w-3" strokeWidth={3} aria-label="concluído" /> : index + 1}
                      </span>
                      <span className="min-w-0">
                        <span
                          className={cn(
                            'block text-sm font-medium',
                            step.done ? 'text-ink-secondary line-through decoration-ink-secondary/40' : 'text-ink'
                          )}
                        >
                          {step.title}
                        </span>
                        {isNext && <span className="block text-xs text-ink-secondary">{step.hint}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            {!isCustom && !hasOwnAccount && (
              <div className="px-4 pt-1">
                <button
                  type="button"
                  onClick={() => navigate(ARENA_OWNER_SIGNUP_AUTO)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-3 py-2 text-sm font-medium text-surface transition-colors hover:bg-ink/90"
                >
                  <WandSparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                  Simular cadastro completo
                </button>
              </div>
            )}

            <footer className="mt-2 border-t border-border/60 px-4 py-3">
              {confirmingReset ? (
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-ink">Apagar conta, arena e progresso?</span>
                  <span className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setConfirmingReset(false)}
                      className="rounded-md px-2 py-1 font-medium text-ink-secondary hover:bg-chip"
                    >
                      Não
                    </button>
                    <button
                      type="button"
                      onClick={restart}
                      className="rounded-md bg-danger px-2 py-1 font-medium text-surface hover:bg-danger/90"
                    >
                      Sim, recomeçar
                    </button>
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingReset(true)}
                  className="flex items-center gap-1.5 text-xs font-medium text-ink-secondary hover:text-danger"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  Recomeçar demonstração
                </button>
              )}
            </footer>
          </motion.section>
        ) : (
          <motion.button
            key="closed"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2, ease: EASE_OUT }}
            className="flex items-center gap-2 rounded-full border border-border/60 bg-surface/95 py-2 pl-3 pr-4 text-sm font-medium text-ink shadow-card-hover backdrop-blur hover:border-primary/40"
            aria-label={`Abrir roteiro da demonstração, ${doneCount} de ${steps.length} concluídos`}
          >
            <MapIcon className="h-4 w-4 text-primary" aria-hidden="true" />
            Roteiro
            <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-semibold text-primary-dark">
              {doneCount}/{steps.length}
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
